// components/TwoFactorVerification.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';

export default function TwoFactorVerification({ 
  show, 
  onClose, 
  onVerify, 
  resendTwoFactorCode, // ✅ Changé de onResend à resendTwoFactorCode
  emailMasked, 
  loading: externalLoading = false,
  resendLoading = false 
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendMessageType, setResendMessageType] = useState('');
  const [internalLoading, setInternalLoading] = useState(false);
  const inputRefs = useRef([]);

  // Reset quand le modal s'ouvre
  useEffect(() => {
    if (show) {
      setCode(['', '', '', '', '', '']);
      setError('');
      setResendMessage('');
      setInternalLoading(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [show]);

  const handleChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');
    const newCode = [...code];
    newCode[index] = numericValue.slice(-1);
    setCode(newCode);
    if (error) setError('');
    
    if (numericValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '');
    const digits = pastedData.split('').slice(0, 6);
    
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) newCode[index] = digit;
    });
    setCode(newCode);
    
    const lastFilledIndex = Math.min(digits.length, 5);
    if (inputRefs.current[lastFilledIndex]) {
      inputRefs.current[lastFilledIndex]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      setError('Veuillez entrer les 6 chiffres du code');
      return;
    }
    
    setInternalLoading(true);
    setError('');
    
    try {
      const result = await onVerify(fullCode);
      console.log('Résultat vérification:', result);
      
      if (result && !result.success) {
        setError(result.message || 'Code invalide. Veuillez réessayer.');
        setCode(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      console.error('Erreur lors de la vérification:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setInternalLoading(false);
    }
  };

  const handleResend = async () => {
    setResendMessage('');
    try {
      // ✅ Appel de resendTwoFactorCode sans paramètre car le userId est déjà dans le contexte
      const result = await resendTwoFactorCode();
      
      if (result.success) {
        setResendMessageType('success');
        setResendMessage(' Nouveau code envoyé avec succès');
        setCode(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        setResendMessageType('danger');
        setResendMessage('❌ ' + (result.message || 'Erreur lors de l\'envoi'));
      }
    } catch (err) {
      setResendMessageType('danger');
      setResendMessage('❌ Erreur de connexion: ' + err);
    }
  };

  const isButtonDisabled = internalLoading || externalLoading;

  return (
    <Modal 
      show={show} 
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      className="two-factor-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="w-100 text-center">
          <div className="two-factor-icon mb-3">
            <div className="icon-circle" style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4361ee20, #4361ee10)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <i className="ri-shield-keyhole-line" style={{ fontSize: '32px', color: '#4361ee' }}></i>
            </div>
          </div>
          <h5 className="fw-bold mb-1" style={{ color: '#4361ee' }}>Double authentification</h5>
          <p className="text-muted small mb-0">
            Un code de vérification a été envoyé à
          </p>
          <p className="text-muted small fw-bold">{emailMasked}</p>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="px-4 py-3">
        <Form onSubmit={handleSubmit}>
          <div className="text-center mb-4">
            <p className="small text-muted">
              Entrez le code à 6 chiffres reçu par email
            </p>
          </div>
          
          {error && (
            <Alert 
              variant="danger" 
              className="mb-3 py-2"
              onClose={() => setError('')}
              dismissible
            >
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}
          
          {resendMessage && (
            <Alert 
              variant={resendMessageType} 
              className="mb-3 py-2"
              onClose={() => setResendMessage('')}
              dismissible
            >
              <i className={`ri-${resendMessageType === 'success' ? 'checkbox-circle' : 'error-warning'}-line me-2`}></i>
              {resendMessage}
            </Alert>
          )}
          
          <div className="d-flex justify-content-center gap-2 mb-4">
            {code.map((digit, index) => (
              <Form.Control
                key={index}
                type="text"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                ref={(el) => (inputRefs.current[index] = el)}
                className="code-input text-center"
                style={{
                  width: '50px',
                  height: '60px',
                  fontSize: '1.5rem',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontWeight: 'bold',
                  color: '#4361ee'
                }}
                maxLength="1"
                disabled={isButtonDisabled}
              />
            ))}
          </div>
          
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="light"
              onClick={onClose}
              disabled={isButtonDisabled}
              className="px-4"
              style={{
                borderRadius: '30px',
                padding: '0.5rem 2rem',
                background: '#f1f5f9',
                border: 'none',
                color: '#64748b'
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isButtonDisabled}
              className="px-4"
              style={{
                borderRadius: '30px',
                padding: '0.5rem 2rem',
                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                border: 'none'
              }}
            >
              {isButtonDisabled ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Vérification...
                </>
              ) : (
                'Vérifier'
              )}
            </Button>
          </div>
          
          <div className="text-center mt-3">
            <Button
              variant="link"
              className="small text-muted p-0"
              style={{ textDecoration: 'none' }}
              onClick={handleResend}
              disabled={resendLoading || isButtonDisabled}
            >
              {resendLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line me-1"></i>
                  Renvoyer le code
                </>
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}